import type { Ref } from 'vue'
import type { EventDescription } from './types/event'
import type { StrapiEventListPage } from './types/strapiEventListPage'

export interface Breadcrumb {
  text: any
  link: string
}

export const useEventBreadcrumbs = (
  isGiftcard = false,
  {
    eventData: kftEvent,
    cityData: strapiCity,
    cityCategoryData: strapiCityCategory,
  }: {
    eventData: Ref<EventDescription | null>
    cityData: Ref<Partial<StrapiEventListPage | null>>
    cityCategoryData: Ref<Partial<StrapiEventListPage | null>>
  }
) => {
  const $device = useDevice()
  const i18n = useI18n()
  const fullLocalizedUrl = useFullLocalizedUrl()

  const breadcrumbs = ref<Breadcrumb[]>([])
  const categoryBreadcrumbs = ref<Breadcrumb[]>([])

  const initCategoryBreadcrumbs = () => {
    const mainCategorySlug = eventGetters.getMainCategorySlug(kftEvent.value)

    return [
      {
        text: i18n.t('general.home'),
        link: fullLocalizedUrl('/'),
      },
      {
        text: eventGetters.getMainCategory(kftEvent.value),
        link: fullLocalizedUrl(`/c/${mainCategorySlug}/`),
      },
      {
        text: eventGetters.getTitle(kftEvent.value),
        link: null,
      },
    ] as Breadcrumb[]
  }

  const initBreadcrumbs = () => {
    const breadcrumbs = []

    breadcrumbs.push({
      text: i18n.t('general.home'),
      link: fullLocalizedUrl('/'),
    })

    const addressSlug = eventGetters.getAddressLocality(kftEvent.value).slug
    const addressName = eventGetters.getAddressLocality(kftEvent.value).name
    const mainCategorySlug = eventGetters.getMainCategorySlug(kftEvent.value)

    const hasCityPage = Boolean(
      strapiEventListPageGetters.getId(strapiCity.value)
    )

    const hasCityCategoryPage = Boolean(
      strapiEventListPageGetters.getId(strapiCityCategory.value)
    )

    /**
     * We only add additional breadcrumbs in desktop or tablets
     * */
    if ($device.isDesktopOrTablet) {
      /* Check if event has a location (aka not online) */
      if (addressSlug && hasCityPage) {
        breadcrumbs.push({
          text: addressName,
          link: fullLocalizedUrl(`/${addressSlug}/`),
        })
      }

      /* If we have category and city combination */
      if (hasCityCategoryPage && addressSlug) {
        breadcrumbs.push({
          text:
            eventGetters.getMainCategory(kftEvent.value) + ' ' + addressName,
          link: fullLocalizedUrl(`/${addressSlug}` + `/${mainCategorySlug}/`),
        })
      } else {
        breadcrumbs.push({
          text: isGiftcard
            ? i18n.t('giftcard.breadcrumb.value')
            : eventGetters.getMainCategory(kftEvent.value),
          link: fullLocalizedUrl(
            isGiftcard ? '/giftcard' : `/c/${mainCategorySlug}/`
          ),
        })
      }
    }

    breadcrumbs.push({
      text: eventGetters.getTitle(kftEvent.value),
      link: fullLocalizedUrl(`/e/${eventGetters.getPermalink(kftEvent.value)}`),
    })
    return breadcrumbs
  }

  breadcrumbs.value = initBreadcrumbs()
  categoryBreadcrumbs.value = initCategoryBreadcrumbs()

  return {
    breadcrumbs: computed(() => breadcrumbs.value),
    categoryBreadcrumbs: computed(() =>
      isGiftcard ? [] : categoryBreadcrumbs.value
    ),
  }
}
